import type * as mdast from "mdast";
import { phrasing } from "mdast-util-phrasing";
import type { Plugin, Transformer } from "unified";
import { CONTINUE, EXIT, visit } from "unist-util-visit";

interface TagPluginOptions {
  handlers: readonly TagHandler[];
  trimsPartialTags?: boolean;
}

export interface TagHandler {
  open: RegExp;
  close: RegExp;
  wrapPhrasingContent: (
    match: RegExpMatchArray,
    children: mdast.StaticPhrasingContent[],
  ) => mdast.PhrasingContent;
}

function checkIsStaticPhrasingContent(
  node: mdast.Content,
): node is mdast.StaticPhrasingContent {
  return (
    phrasing(node) && node.type !== "link" && node.type !== "linkReference"
  );
}

export function createTagPlugin(options: TagPluginOptions): Plugin {
  const transformer: Transformer = (tree) => {
    const root = tree as mdast.Root;

    // Parse HTML tags
    visit(root, "html", (node, index, parent) => {
      // Check this is not the root
      if (index === undefined || parent === undefined) {
        return CONTINUE;
      }

      for (const handler of options.handlers) {
        // Look for an open tag
        const openMatch = node.value.match(handler.open);
        if (!openMatch) {
          continue;
        }

        // Look for a close tag
        const closeIndex = parent.children.findIndex(
          (sibling, siblingIndex) =>
            siblingIndex > index &&
            sibling.type === "html" &&
            sibling.value.match(handler.close),
        );
        if (closeIndex === -1) {
          continue;
        }

        // Gather the nodes between the open and close tags
        const content = parent.children.slice(index + 1, closeIndex);

        // Filter down to phrasing content
        const phrasingContent = content.filter(checkIsStaticPhrasingContent);
        const nonPhrasingContent = content.filter(
          (node) => !checkIsStaticPhrasingContent(node),
        );

        const wrapper = handler.wrapPhrasingContent(openMatch, phrasingContent);

        // Replace the open tag up to and including the close tag with the link and any non-phrasing content
        parent.children.splice(
          index,
          closeIndex - index + 1,
          wrapper,
          ...nonPhrasingContent,
        );

        // Re-visit the new link and its new descendents
        return [CONTINUE, index];
      }

      return CONTINUE;
    });

    // Remove any trailing partial HTML tags that might arise from streaming
    if (options.trimsPartialTags) {
      // Visit only the last text node in the document
      visit(
        root,
        "text",
        (node, index, parent) => {
          node.value = trimPartialTags(node.value);
          return EXIT;
        },
        true,
      );
    }
  };

  return () => transformer;
}

export function trimPartialTags(text: string) {
  // Trim any solo "<" or "</" or "<tag whatever" without a closing ">", up to
  // a maximum length to avoid regex DOS. Keep the cap comfortably above the
  // longest tag we stream: a quote open tag carrying a base64 `source`
  // marker runs ~220 chars, and an untrimmed partial tag flashes raw base64
  // as visible text mid-stream.
  return text.replace(/<\/?([a-z][^>]{0,512})?$/i, "");
}
